import type {
  GetListParams,
  GetListResult,
  CreateResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
} from "ra-core";
import { toRaConstructionSite, type ConstructionSiteRaRecord } from "./Mappers";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { ConstructionSiteService } from "@/domain/port/ConstructionSiteService";
import { asConstructionSiteId } from "@/domain/model/Ids";
import type { UpdateConstructionSiteCommand } from "@/domain/model/ConstructionSite";

export function makeConstructionSiteAdapter(
  constructionSites: ConstructionSiteService,
) {
  return {
    async getList(
      _resource: string,
      params: GetListParams,
    ): Promise<GetListResult<ConstructionSiteRaRecord>> {
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? {
        field: "createdAt",
        order: "DESC",
      };
      const filter = params.filter ?? {};
      const q: PageQuery = {
        page,
        pageSize: perPage,
        sortBy: field,
        sortDir: order.toLowerCase() as "asc" | "desc",
        filter,
      };
      const res = await constructionSites.list(q);
      return { data: res.items.map(toRaConstructionSite), total: res.total };
    },

    async create(
      _resource: string,
      { data }: { data: any },
    ): Promise<CreateResult<ConstructionSiteRaRecord>> {
      const created = await constructionSites.create(data);
      return { data: toRaConstructionSite(created) };
    },

    async getMany(
      _resource: string,
      params: GetManyParams,
    ): Promise<GetManyResult<ConstructionSiteRaRecord>> {
      const ids = params.ids.map(asConstructionSiteId);
      const items = await constructionSites.getManyByIds(ids);
      return { data: items.map(toRaConstructionSite) };
    },

    async getOne(
      _resource: string,
      params: GetOneParams,
    ): Promise<GetOneResult<ConstructionSiteRaRecord>> {
      const item = await constructionSites.getById(
        asConstructionSiteId(params.id),
      );
      return { data: toRaConstructionSite(item) };
    },

    async update(
      _resource: string,
      params: UpdateParams,
    ): Promise<UpdateResult<ConstructionSiteRaRecord>> {
      const updated = await constructionSites.update(
        asConstructionSiteId(params.id),
        params.data as UpdateConstructionSiteCommand,
      );
      return { data: toRaConstructionSite(updated) };
    },

    async delete(
      _resource: string,
      params: DeleteParams,
    ): Promise<DeleteResult<ConstructionSiteRaRecord>> {
      const deleted = await constructionSites.delete(
        asConstructionSiteId(params.id),
      );
      return { data: toRaConstructionSite(deleted) };
    },
  };
}
